import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { DevicePairingCard } from "./DevicePairingCard";
import { EmployeeInviteCard } from "./EmployeeInviteCard";
import { getWorkforceDeviceSnapshot, subscribeWorkforceDevice } from "./devicePayload";
import { useDevice } from "./hooks/useDevice";

function useLiveWorkforceDevice(workforceUid, device) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!workforceUid) return undefined;
    return subscribeWorkforceDevice(workforceUid, () => setTick((value) => value + 1));
  }, [workforceUid]);

  if (!workforceUid) return device;
  const published = getWorkforceDeviceSnapshot(workforceUid);
  return published === undefined ? device : published;
}

export const DeviceTracker = ({ workforce, deviceTrackerUid, device, setDevice, getDeviceById }) => {
  const liveDevice = useLiveWorkforceDevice(workforce?.uid, device);
  const { companies } = useSelector((s) => s.userState);

  const token = sessionStorage.getItem("token");
  const companyId = companies?.[0]?.companyId;
  const workforceUid = workforce?.uid;
  const companyName = companies?.[0]?.company?.businessName || "Your company";

  const employeeFullName = useMemo(
    () => `${workforce?.firstName || ""} ${workforce?.lastName || ""}`.trim(),
    [workforce?.firstName, workforce?.lastName],
  );

  const {
    loading,
    sendingInvitation,
    result,
    deleted,
    deviceLoaded,
    isQRHovered,
    setIsQRHovered,
    emailSent,
    qrCodeRef,
    handleCreate,
    handleDelete,
    handleDownloadQR,
    handleSendDownloadEmail,
  } = useDevice({
    workforceUid,
    deviceTrackerUid: liveDevice?.uid || deviceTrackerUid,
    device: liveDevice,
    setDevice,
    token,
    companyId,
    getDeviceById,
  });

  const showStartPairing = deviceLoaded && !liveDevice;

  if (liveDevice === undefined) {
    return (
      <Box sx={{ p: "1.5rem", textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: "1rem", fontSize: "0.875rem" }}>Loading device tracker status...</Typography> {/* Reduced */}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        // bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.6)",
      }}
    >
      <Grid container spacing={{ xs: 1, md: 1 }}>
        {/* Slightly tighter spacing */}
        {/* Device Pairing */}
        <Grid item xs={12} md={7.5}>
          <DevicePairingCard
            device={liveDevice}
            deviceTrackerUid={deviceTrackerUid}
            loading={loading}
            handleCreate={handleCreate}
            handleDelete={handleDelete}
            handleDownloadQR={handleDownloadQR}
            isQRHovered={isQRHovered}
            setIsQRHovered={setIsQRHovered}
            qrCodeRef={qrCodeRef}
            deleted={deleted}
            deviceLoaded={deviceLoaded}
            showStartPairing={showStartPairing}
          />
        </Grid>
        {/* Invite Employee */}
        <Grid item xs={12} md={4.5}>
          <EmployeeInviteCard
            workforce={workforce}
            companyName={companyName}
            employeeFullName={employeeFullName}
            sendingInvitation={sendingInvitation}
            emailSent={emailSent}
            result={result}
            handleSendDownloadEmail={handleSendDownloadEmail}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
